// @ts-nocheck
import { useRef, useState } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { Box, ButtonBase, CardContent, ClickAwayListener, Divider, Grid, Paper, Popper, Stack, Tooltip, Typography } from '@mui/material';
import Avatar from 'components/@extended/Avatar';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';
import IconButton from 'components/@extended/IconButton';
import useAuth from 'hooks/useAuth';
import { useMe } from 'services/partner/api';
import { formatDate, humanize } from 'utils/format';
import { LogoutOutlined } from '@ant-design/icons';
import { ThemeMode } from 'types/config';

// front-end's header Profile button and popper, showing the partner login instead of the
// business user (no avatar upload, no workspace switch).
const Profile = () => {
  const theme = useTheme();
  const anchorRef = useRef<any>(null);
  const [open, setOpen] = useState(false);
  const { logout, user } = useAuth();
  const { data: me } = useMe();

  const who = me?.user ?? user;
  const name = who?.display_name || who?.email || 'Partner';
  const initial = name.charAt(0).toUpperCase();

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? 'grey.200' : 'grey.300';

  const row = (label: string, value: string) => (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="textSecondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }} noWrap>
        {value}
      </Typography>
    </Stack>
  );

  return (
    <Box sx={{ flexShrink: 0 }}>
      <ButtonBase
        sx={{
          px: 0.75,
          py: 0.2,
          bgcolor: open ? iconBackColorOpen : alpha(theme.palette.grey[500], 0.06),
          borderRadius: '12px',
          border: '1px solid',
          borderColor: alpha(theme.palette.grey[500], 0.12),
          height: 38,
          minWidth: 38,
          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.secondary.dark}`,
            outlineOffset: 2
          }
        }}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={() => setOpen((o) => !o)}
      >
        <Stack direction="row" spacing={0.85} alignItems="center" justifyContent="center" sx={{ px: 0.15 }}>
          <Avatar alt="profile user" size="xs">
            {initial}
          </Avatar>
          <Box sx={{ display: { xs: 'none', xl: 'block' }, textAlign: 'left', maxWidth: 116 }}>
            <Typography
              sx={{
                fontSize: '0.78rem',
                fontWeight: 700,
                color: 'var(--nv-text-primary)',
                lineHeight: 1.1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {name}
            </Typography>
          </Box>
        </Stack>
      </ButtonBase>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper
              sx={{
                boxShadow: '0 24px 50px var(--nv-border-strong)',
                border: '1px solid var(--nv-border-strong)',
                borderRadius: '22px',
                width: 312,
                minWidth: 240,
                maxWidth: 312,
                [theme.breakpoints.down('md')]: { maxWidth: 250 }
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false}>
                  <CardContent
                    sx={{ px: 2.5, pt: 3, pb: 2.25, background: 'linear-gradient(180deg, var(--nv-surface) 0%, var(--nv-surface-softer) 100%)' }}
                  >
                    <Grid container justifyContent="space-between" alignItems="center" wrap="nowrap">
                      <Grid item sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Avatar alt="profile user" sx={{ width: 32, height: 32 }}>
                            {initial}
                          </Avatar>
                          <Stack sx={{ minWidth: 0 }}>
                            <Typography variant="h6" noWrap>
                              {me?.partner.name ?? ''}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" noWrap>
                              {name}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Grid>
                      <Grid item>
                        <Tooltip title="Logout">
                          <IconButton
                            size="large"
                            sx={{ color: 'text.primary', borderRadius: '14px', border: '1px solid var(--nv-border-strong)' }}
                            onClick={logout}
                          >
                            <LogoutOutlined />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </CardContent>
                  <Divider />
                  <CardContent sx={{ px: 2.5, py: 2 }}>
                    <Stack spacing={1}>
                      {who?.email && row('Email', who.email)}
                      {who?.role && row('Role', humanize(who.role))}
                      {who?.last_login_at && row('Last sign-in', formatDate(who.last_login_at))}
                    </Stack>
                  </CardContent>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default Profile;
